import {
  DirectFlightTipData,
  EcoAccommodationTipData,
  ReductionTip,
  TipType,
  TransportAlternativeTipData,
} from '../models/reduction-tip';

/** Alternatives proposed by a `transport_alternative` tip. */
export const ALTERNATIVE_MODES = ['train', 'carpooling', 'bus'] as const;
export type AlternativeMode = (typeof ALTERNATIVE_MODES)[number];

/** Lucide icon of each tip type, for the collapsed row. */
export const TIP_ICON: Record<TipType, string> = {
  transport_alternative: 'tram-front',
  direct_flight: 'plane',
  trip_duration: 'calendar',
  eco_accommodation: 'building',
  high_emission_activity: 'zap',
};

/** i18n sub-key of each tip type under `reductionTips.*`. */
export const TIP_I18N_KEY: Record<TipType, string> = {
  transport_alternative: 'transportAlternative',
  direct_flight: 'directFlight',
  trip_duration: 'tripDuration',
  eco_accommodation: 'ecoAccommodation',
  high_emission_activity: 'highEmissionActivity',
};

/**
 * Tip types carrying a quantified saving. The others (`trip_duration`,
 * `high_emission_activity`) only change the daily intensity or flag a hotspot,
 * so they stay informational and cannot be simulated.
 */
const SELECTABLE_TIP_TYPES: readonly TipType[] = [
  'transport_alternative',
  'direct_flight',
  'eco_accommodation',
];

export function isSelectableTip(tip: ReductionTip): boolean {
  return SELECTABLE_TIP_TYPES.includes(tip.type);
}

export interface TipSaving {
  /** Breakdown key the saving applies to: 'plane', 'accommodationImpact'… */
  overviewType: string | null;
  /** kg CO₂e avoided over the whole trip (all travellers). */
  savings: number;
  /** Trip element concerned, used to deduplicate overlapping tips. */
  elementId: string | null;
}

export interface TipSavingContext {
  travellers: number;
  /** Maps a transport type to its breakdown category ('plane', 'train'…). */
  transportCategoryOf?: (transportType: string) => string | undefined;
}

export function getAlternativeEmissionsPerPerson(
  data: TransportAlternativeTipData,
  mode: AlternativeMode,
): number {
  switch (mode) {
    case 'train':
      return data.trainEmissionsPerPerson;
    case 'carpooling':
      return data.carpoolingEmissionsPerPerson;
    case 'bus':
      return data.busEmissionsPerPerson;
  }
}

/** Alternative saving the most CO₂e — the one preselected for the user. */
export function getBestAlternativeMode(
  data: TransportAlternativeTipData,
): AlternativeMode {
  return ALTERNATIVE_MODES.reduce((best, mode) =>
    getAlternativeEmissionsPerPerson(data, mode) <
    getAlternativeEmissionsPerPerson(data, best)
      ? mode
      : best,
  );
}

/**
 * Saving of a single tip, expressed on the same basis as
 * `getOverviewDetailedBreakdown()` — i.e. totals for the whole trip.
 *
 * The API is not homogeneous: `transport_alternative` and `direct_flight`
 * carry per-traveller values (their mocks are computed with `travellers = 1`)
 * while `eco_accommodation` already carries trip totals.
 */
export function getTipSaving(
  tip: ReductionTip,
  ctx: TipSavingContext,
  mode?: AlternativeMode,
): TipSaving {
  const travellers = Math.max(1, ctx.travellers);

  switch (tip.type) {
    case 'transport_alternative': {
      const data = tip.data as TransportAlternativeTipData;
      const chosen = mode ?? getBestAlternativeMode(data);
      const perPerson =
        data.currentEmissionsPerPerson -
        getAlternativeEmissionsPerPerson(data, chosen);
      return {
        overviewType: transportOverviewType(data.transportType, ctx),
        savings: Math.max(0, perPerson) * travellers,
        elementId: data.transportId,
      };
    }
    case 'direct_flight': {
      const data = tip.data as DirectFlightTipData;
      return {
        overviewType: transportOverviewType(data.transportType, ctx),
        savings: Math.max(0, data.savings) * travellers,
        elementId: data.transportId,
      };
    }
    case 'eco_accommodation': {
      const data = tip.data as EcoAccommodationTipData;
      return {
        overviewType: 'accommodationImpact',
        savings: Math.max(0, data.savings),
        elementId: data.accommodationId,
      };
    }
    default:
      return { overviewType: null, savings: 0, elementId: null };
  }
}

/**
 * One tip per trip element, keeping the largest saving. A multi-stop flight
 * gets both an alternative and a direct-flight tip, and the two rewrite that
 * transport in incompatible ways — this is the arbitration
 * `sumSavingsByOverviewType()` makes on the figures, so what is applied to the
 * trip is what the donut simulated.
 */
export function pickBestTipPerElement(
  tips: ReductionTip[],
  ctx: TipSavingContext,
  modes: Record<string, AlternativeMode>,
): ReductionTip[] {
  const bestByElement = new Map<
    string,
    { tip: ReductionTip; savings: number }
  >();

  tips.forEach((tip, index) => {
    const saving = getTipSaving(tip, ctx, modes[tip.id]);
    if (!saving.overviewType || saving.savings <= 0) return;

    const key = saving.elementId ?? `#${index}`;
    const current = bestByElement.get(key);
    if (!current || saving.savings > current.savings) {
      bestByElement.set(key, { tip, savings: saving.savings });
    }
  });

  return [...bestByElement.values()].map((entry) => entry.tip);
}

/**
 * Total avoided per breakdown category. Two tips can target the same trip
 * element (a multi-stop flight gets both an alternative and a direct-flight
 * tip); only the largest saving of each element counts, otherwise the same
 * flight would be reduced twice.
 */
export function sumSavingsByOverviewType(
  savings: TipSaving[],
): Record<string, number> {
  const bestByElement = new Map<string, TipSaving>();

  savings.forEach((saving, index) => {
    if (!saving.overviewType || saving.savings <= 0) return;
    const key = saving.elementId ?? `#${index}`;
    const current = bestByElement.get(key);
    if (!current || saving.savings > current.savings) {
      bestByElement.set(key, saving);
    }
  });

  const totals: Record<string, number> = {};
  bestByElement.forEach((saving) => {
    const type = saving.overviewType!;
    totals[type] = (totals[type] ?? 0) + saving.savings;
  });
  return totals;
}

/**
 * Both transport tips only ever target flights, so 'plane' is the safe
 * fallback when the category cannot be resolved.
 */
function transportOverviewType(
  transportType: string,
  ctx: TipSavingContext,
): string {
  return ctx.transportCategoryOf?.(transportType) ?? 'plane';
}
